/* ============================================================================
 * ตัวช่วยเรื่อง "ข้อมูลโครงการ" — ใช้ร่วมกันระหว่าง
 *   - จัดการทรัพย์ › เพิ่ม/แก้ไขทรัพย์ (page/admin/asset_create.js) ขั้นที่ 1
 *   - จัดการทรัพย์ › โครงการ         (page/admin/projects.js)
 *
 * ทั้งสองหน้ามีช่องค้นหาสถานที่จากแผนที่ชุดเดียวกัน และต้องตอบคำถามเดียวกันคือ
 * "สถานที่ที่เพิ่งเลือกมา มีอยู่ในข้อมูลโครงการแล้วหรือยัง"
 *   มีแล้ว   → อัปเดตตำแหน่งที่ตั้งของโครงการเดิม (ไม่สร้างซ้ำ เพราะชื่อโครงการห้ามซ้ำ)
 *   ยังไม่มี → สร้างโครงการใหม่ให้ตอนกดบันทึก
 * ตัวเทียบชื่อกับตัวประกอบ payload จึงต้องอยู่ที่เดียว ไม่งั้นสองหน้าจะตัดสินคนละแบบ
 * ========================================================================== */

import { GET } from "./CustomComponent.js";

/**
 * ชื่อโครงการที่ใช้เก็บ/เทียบจริง — ตัดหัวท้ายและยุบช่องว่างซ้อนให้เหลือช่องเดียว
 * สูตรเดียวกับ normalizeName ฝั่ง backend (modules/admin/project/project.service.js)
 */
export const normalizeName = (value) => String(value ?? "").trim().replace(/\s+/g, " ");

/** ชื่อเดียวกันไหม — ไม่สนตัวพิมพ์ ไม่สนช่องว่างเกิน ตรงกับ unique index ในฐานข้อมูล */
export const sameName = (a, b) => {
  const left = normalizeName(a).toLowerCase();
  const right = normalizeName(b).toLowerCase();
  return !!left && left === right;
};

/**
 * โครงการที่ชื่อตรงกับที่ส่งมา (ถ้ามี) — ถามฝั่ง API ไม่ใช่เทียบกับรายการในหน้า
 *
 * ตารางโหลดมาทีละหน้า และตัวเลือกในฟอร์มเพิ่มทรัพย์มีเฉพาะโครงการที่เปิดใช้งาน
 * จึงเทียบกับ state ในหน้าไม่ได้ (จะพลาดโครงการที่ปิดใช้งานอยู่ แล้วไปสร้างชื่อซ้ำ)
 * data_search จับชื่อแบบ "มีคำนี้อยู่ข้างใน" จึงต้องกรองซ้ำฝั่งนี้ให้เหลือชื่อที่ตรงเป๊ะ
 *
 * @param {string} name ชื่อสถานที่ / ชื่อโครงการที่ต้องการหา
 * @returns {Promise<object|null>} record โครงการเต็ม ๆ (ตาม project.response.dto) หรือ null
 */
export const findProjectByName = async (name) => {
  const keyword = normalizeName(name);
  if (!keyword) return null;

  const result = await GET("admin/projects", { page: 1, page_size: 50, data_search: keyword });
  if (!result?.status) return null;

  const list = result.data || [];
  // ชื่อไทยก่อนเสมอ (unique index ผูกกับคอลัมน์นี้) แล้วค่อยเผื่อกรณีที่ค้นมาเป็นชื่ออังกฤษ
  return list.find((item) => sameName(item.name, keyword)) || list.find((item) => sameName(item.name_en, keyword)) || null;
};

/* ช่องที่ปล่อยว่างต้องส่งเป็น "-" เพราะ removeEmptyString ตัดค่า ""/null ทิ้งก่อนส่ง
   ซึ่งจะกลายเป็น "ไม่ได้แก้ฟิลด์นี้" แทนที่จะเป็น "ล้างค่า" (ระหว่างทางแปลงกลับเป็น "") */
export const numOrDash = (value) => (String(value ?? "").trim() === "" ? "-" : Number(value));
export const textOrDash = (value) => (String(value ?? "").trim() === "" ? "-" : String(value).trim());

/**
 * ส่วน "ตำแหน่งที่ตั้ง" ของ payload โครงการ — ชุดฟิลด์เดียวกับที่ฟอร์มทรัพย์กรอก
 * @param {object} d ค่าจากฟอร์ม (ใช้ได้ทั้ง state.data ของหน้าทรัพย์ และ state.form ของหน้าโครงการ)
 */
export const projectLocationBody = (d) => ({
  province: textOrDash(d.province),
  district: textOrDash(d.district),
  subdistrict: textOrDash(d.subdistrict),
  soi: textOrDash(d.soi),
  postcode: textOrDash(d.postcode),
  latitude: numOrDash(d.latitude),
  longitude: numOrDash(d.longitude),
  // ตัดแถวที่ยังไม่ได้ตั้งชื่อทิ้ง ไม่งั้นจะได้จุดสนใจเปล่า ๆ ติดไปกับโครงการ
  nearby: (d.nearby || [])
    .filter((e) => String(e.label || "").trim())
    .map((e) => ({ type: e.type, label: e.label.trim(), distance: numOrDash(e.distance) })),
});
